<template>
  <div class="user-management-container">
    <!-- Page Header -->
    <div class="page-header">
      <h2 class="page-title">{{ $t('userManagement.title') }}</h2>
      <button v-if="activeTab === 'users'" class="btn-primary" @click="showAddUserForm = true">
        <i class="fas fa-plus"></i> {{ $t('userManagement.addUser') }}
      </button>
    </div>

    <!-- Tab Buttons -->
    <div class="tab-container">
      <button
        :class="['tab-btn', activeTab === 'users' ? 'tab-btn-active' : '']"
        @click="switchTab('users')"
      >
        <i class="fas fa-users"></i> {{ $t('userManagement.title') }}
      </button>
      <button
        :class="['tab-btn', activeTab === 'archive' ? 'tab-btn-active' : '']"
        @click="switchTab('archive')"
      >
        <i class="fas fa-archive"></i> {{ $t('userManagement.archive') }}
      </button>
    </div>

    <!-- ===================== USERS TAB ===================== -->
    <div v-if="activeTab === 'users'">
      <!-- Search Bar -->
      <div class="search-container">
        <i class="fas fa-search search-icon"></i>
        <input 
          type="text" 
          class="search-input" 
          :placeholder="$t('userManagement.searchUsers')" 
          v-model="searchQuery"
          @input="fetchUserSuggestions"
          @focus="showSuggestions = true"
          @keyup.enter="searchUsers"
        />
        <button class="btn-search" @click="searchUsers()">
          {{ $t('userManagement.search') }}
        </button>
        
        <div class="search-suggestions" v-if="showSuggestions && searchSuggestions.length > 0">
          <div 
            v-for="user in searchSuggestions" 
            :key="user.id" 
            class="suggestion-item"
            @click="selectUser(user)"
          >
            <div class="suggestion-name">
              {{ user.first_name }} {{ user.last_name }}
              <span :class="['role-tag', `role-${user.role}`]">{{ $t(`userManagement.${user.role}`) }}</span>
            </div>
            <div class="suggestion-details">
              <span class="suggestion-username">@{{ user.username }}</span>
              <span class="suggestion-email">{{ user.email }}</span>
            </div>
          </div>
        </div>
      </div>

      <!-- User Table -->
      <div class="table-responsive" v-if="users.length">
        <table class="user-table">
          <thead>
            <tr>
              <th @click="sortUsers('username')">
                {{ $t('userManagement.username') }}
                <span class="sort-indicator" :class="{'active': sortKey === 'username'}">
                  <i class="fas" :class="sortKey === 'username' && sortOrder === 1 ? 'fa-sort-up' : sortKey === 'username' && sortOrder === -1 ? 'fa-sort-down' : 'fa-sort'"></i>
                </span>
              </th>
              <th @click="sortUsers('first_name')">
                {{ $t('userManagement.firstName') }}
                <span class="sort-indicator" :class="{'active': sortKey === 'first_name'}">
                  <i class="fas" :class="sortKey === 'first_name' && sortOrder === 1 ? 'fa-sort-up' : sortKey === 'first_name' && sortOrder === -1 ? 'fa-sort-down' : 'fa-sort'"></i>
                </span>
              </th>
              <th @click="sortUsers('last_name')">
                {{ $t('userManagement.lastName') }}
                <span class="sort-indicator" :class="{'active': sortKey === 'last_name'}">
                  <i class="fas" :class="sortKey === 'last_name' && sortOrder === 1 ? 'fa-sort-up' : sortKey === 'last_name' && sortOrder === -1 ? 'fa-sort-down' : 'fa-sort'"></i>
                </span>
              </th>
              <th @click="sortUsers('email')">
                {{ $t('userManagement.email') }}
                <span class="sort-indicator" :class="{'active': sortKey === 'email'}">
                  <i class="fas" :class="sortKey === 'email' && sortOrder === 1 ? 'fa-sort-up' : sortKey === 'email' && sortOrder === -1 ? 'fa-sort-down' : 'fa-sort'"></i>
                </span>
              </th>
              <th @click="sortUsers('role')">
                {{ $t('userManagement.role') }}
                <span class="sort-indicator" :class="{'active': sortKey === 'role'}">
                  <i class="fas" :class="sortKey === 'role' && sortOrder === 1 ? 'fa-sort-up' : sortKey === 'role' && sortOrder === -1 ? 'fa-sort-down' : 'fa-sort'"></i>
                </span>
              </th>
              <th @click="sortUsers('status')">
                {{ $t('userManagement.status') }}
                <span class="sort-indicator" :class="{'active': sortKey === 'status'}">
                  <i class="fas" :class="sortKey === 'status' && sortOrder === 1 ? 'fa-sort-up' : sortKey === 'status' && sortOrder === -1 ? 'fa-sort-down' : 'fa-sort'"></i>
                </span>
              </th>
              <th @click="sortUsers('balance')">
                {{ $t('userManagement.balance') }}
                <span class="sort-indicator" :class="{'active': sortKey === 'balance'}">
                  <i class="fas" :class="sortKey === 'balance' && sortOrder === 1 ? 'fa-sort-up' : sortKey === 'balance' && sortOrder === -1 ? 'fa-sort-down' : 'fa-sort'"></i>
                </span>
              </th>
              <th>{{ $t('userManagement.actions') }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="user in sortedUsers" :key="user.id">
              <td>{{ user.username }}</td>
              <td>{{ user.first_name }}</td>
              <td>{{ user.last_name }}</td>
              <td>{{ user.email }}</td>
              <td>
                <span :class="['role-badge', `role-${user.role}`]">
                  {{ $t(`userManagement.${user.role}`) }}
                </span>
              </td>
              <td>
                <span :class="['status-badge', `status-${user.status?.toLowerCase() || 'unknown'}`]">
                  {{ user.status || 'Unknown' }}
                </span>
              </td>
              <td>
                <span :class="['balance-cell', 
                  user.balance > 0 ? 'balance-positive' : 
                  user.balance < 0 ? 'balance-negative' : 'balance-zero']">
                  {{ user.balance }} UAH
                </span>
              </td>
              <td>
                <div class="action-buttons">
                  <button class="btn-action btn-edit" @click="startEditUser(user)">
                    <i class="fas fa-edit"></i> {{ $t('userManagement.edit') }}
                  </button>
                  <button class="btn-action btn-password" @click="startChangePassword(user)">
                    <i class="fas fa-key"></i> {{ $t('userManagement.changePassword') }}
                  </button>
                  <button class="btn-action btn-delete" @click="deleteUser(user.id)">
                    <i class="fas fa-archive"></i> {{ $t('userManagement.delete') }}
                  </button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Empty State -->
      <div class="empty-state" v-else>
        <i class="fas fa-users-slash"></i>
        <p>{{ $t('userManagement.noUsersFound') }}</p>
      </div>

      <!-- Pagination -->
      <div class="pagination-container">
        <div class="pagination-controls">
          <div class="per-page-selector">
            <label for="page-size">{{ $t('userManagement.perPage') }}</label>
            <select id="page-size" v-model="pageSize" @change="handlePageSizeChange" class="page-size-select">
              <option value="5">5</option>
              <option value="10">10</option>
              <option value="25">25</option>
              <option value="50">50</option>
              <option value="100">100</option>
            </select>
          </div>
          
          <div class="page-info">
            {{ $t('userManagement.showing') }} 
            {{ users.length === 0 ? 0 : (currentPage - 1) * pageSize + 1 }}-{{ Math.min(currentPage * pageSize, totalUsers) }} 
            {{ $t('userManagement.of') }} {{ totalUsers }}
          </div>
        </div>
        
        <div class="pagination-nav">
          <button @click="prevPage" :disabled="currentPage === 1" class="page-btn">
            <i class="fas fa-chevron-left"></i> {{ $t('userManagement.previous') }}
          </button>
          <span class="page-status">{{ $t('userManagement.page') }} {{ currentPage }} / {{ totalPages }}</span>
          <button @click="nextPage" :disabled="currentPage >= totalPages" class="page-btn">
            {{ $t('userManagement.next') }} <i class="fas fa-chevron-right"></i>
          </button>
        </div>
      </div>
    </div>

    <!-- ===================== ARCHIVE TAB ===================== -->
    <div v-if="activeTab === 'archive'">
      <!-- Archive Search Bar -->
      <div class="search-container">
        <i class="fas fa-search search-icon"></i>
        <input 
          type="text" 
          class="search-input" 
          :placeholder="$t('userManagement.searchUsers')" 
          v-model="archiveSearchQuery"
          @keyup.enter="fetchArchivedUsers"
        />
        <button class="btn-search" @click="fetchArchivedUsers()">
          {{ $t('userManagement.search') }}
        </button>
      </div>

      <!-- Archive Table -->
      <div class="table-responsive" v-if="!archiveLoading && archivedUsers.length">
        <table class="user-table">
          <thead>
            <tr>
              <th @click="sortArchive('username')">
                {{ $t('userManagement.username') }}
                <span class="sort-indicator" :class="{'active': archiveSortKey === 'username'}">
                  <i class="fas" :class="archiveSortKey === 'username' && archiveSortOrder === 'ASC' ? 'fa-sort-up' : archiveSortKey === 'username' && archiveSortOrder === 'DESC' ? 'fa-sort-down' : 'fa-sort'"></i>
                </span>
              </th>
              <th @click="sortArchive('first_name')">
                {{ $t('userManagement.firstName') }}
                <span class="sort-indicator" :class="{'active': archiveSortKey === 'first_name'}">
                  <i class="fas" :class="archiveSortKey === 'first_name' && archiveSortOrder === 'ASC' ? 'fa-sort-up' : archiveSortKey === 'first_name' && archiveSortOrder === 'DESC' ? 'fa-sort-down' : 'fa-sort'"></i>
                </span>
              </th>
              <th @click="sortArchive('last_name')">
                {{ $t('userManagement.lastName') }}
                <span class="sort-indicator" :class="{'active': archiveSortKey === 'last_name'}">
                  <i class="fas" :class="archiveSortKey === 'last_name' && archiveSortOrder === 'ASC' ? 'fa-sort-up' : archiveSortKey === 'last_name' && archiveSortOrder === 'DESC' ? 'fa-sort-down' : 'fa-sort'"></i>
                </span>
              </th>
              <th @click="sortArchive('email')">
                {{ $t('userManagement.email') }}
                <span class="sort-indicator" :class="{'active': archiveSortKey === 'email'}">
                  <i class="fas" :class="archiveSortKey === 'email' && archiveSortOrder === 'ASC' ? 'fa-sort-up' : archiveSortKey === 'email' && archiveSortOrder === 'DESC' ? 'fa-sort-down' : 'fa-sort'"></i>
                </span>
              </th>
              <th @click="sortArchive('role')">
                {{ $t('userManagement.role') }}
                <span class="sort-indicator" :class="{'active': archiveSortKey === 'role'}">
                  <i class="fas" :class="archiveSortKey === 'role' && archiveSortOrder === 'ASC' ? 'fa-sort-up' : archiveSortKey === 'role' && archiveSortOrder === 'DESC' ? 'fa-sort-down' : 'fa-sort'"></i>
                </span>
              </th>
              <th>{{ $t('userManagement.balance') }}</th>
              <th @click="sortArchive('archived_at')">
                {{ $t('userManagement.archivedAt') }}
                <span class="sort-indicator" :class="{'active': archiveSortKey === 'archived_at'}">
                  <i class="fas" :class="archiveSortKey === 'archived_at' && archiveSortOrder === 'ASC' ? 'fa-sort-up' : archiveSortKey === 'archived_at' && archiveSortOrder === 'DESC' ? 'fa-sort-down' : 'fa-sort'"></i>
                </span>
              </th>
              <th>{{ $t('userManagement.actions') }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="user in archivedUsers" :key="user.id">
              <td>{{ user.username }}</td>
              <td>{{ user.first_name }}</td>
              <td>{{ user.last_name }}</td>
              <td>{{ user.email }}</td>
              <td>
                <span :class="['role-badge', `role-${user.role}`]">
                  {{ $t(`userManagement.${user.role}`) }}
                </span>
              </td>
              <td>
                <span :class="['balance-cell', 
                  user.balance > 0 ? 'balance-positive' : 
                  user.balance < 0 ? 'balance-negative' : 'balance-zero']">
                  {{ user.balance }} UAH
                </span>
              </td>
              <td>{{ new Date(user.archived_at).toLocaleDateString() }}</td>
              <td>
                <div class="action-buttons">
                  <button class="btn-action btn-restore" @click="restoreUser(user.id)">
                    <i class="fas fa-undo"></i> {{ $t('userManagement.restoreUser') }}
                  </button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Archive Loading State -->
      <div class="empty-state" v-else-if="archiveLoading">
        <i class="fas fa-spinner fa-spin"></i>
        <p>{{ $t('userManagement.loading') }}</p>
      </div>

      <!-- Archive Empty State -->
      <div class="empty-state" v-else>
        <i class="fas fa-archive"></i>
        <p>{{ $t('userManagement.noArchivedUsers') }}</p>
      </div>

      <!-- Archive Pagination -->
      <div class="pagination-container" v-if="archivedUsers.length || archiveTotalUsers > 0">
        <div class="pagination-controls">
          <div class="per-page-selector">
            <label for="archive-page-size">{{ $t('userManagement.perPage') }}</label>
            <select id="archive-page-size" v-model="archivePageSize" @change="handleArchivePageSizeChange" class="page-size-select">
              <option value="5">5</option>
              <option value="10">10</option>
              <option value="25">25</option>
              <option value="50">50</option>
              <option value="100">100</option>
            </select>
          </div>

          <div class="page-info">
            {{ $t('userManagement.showing') }}
            {{ archiveTotalUsers === 0 ? 0 : (archiveCurrentPage - 1) * archivePageSize + 1 }}-{{ Math.min(archiveCurrentPage * archivePageSize, archiveTotalUsers) }}
            {{ $t('userManagement.of') }} {{ archiveTotalUsers }}
          </div>
        </div>

        <div class="pagination-nav">
          <button @click="archivePrevPage" :disabled="archiveCurrentPage === 1" class="page-btn">
            <i class="fas fa-chevron-left"></i> {{ $t('userManagement.previous') }}
          </button>
          <span class="page-status">{{ $t('userManagement.page') }} {{ archiveCurrentPage }} / {{ archiveTotalPages }}</span>
          <button @click="archiveNextPage" :disabled="archiveCurrentPage >= archiveTotalPages" class="page-btn">
            {{ $t('userManagement.next') }} <i class="fas fa-chevron-right"></i>
          </button>
        </div>
      </div>
    </div>

    <!-- Add User Form -->
    <div class="overlay" v-if="showAddUserForm">
      <div class="form-popup">
        <div class="form-header">
          <h2>{{ $t('userManagement.addNewUser') }}</h2>
          <button class="btn-close" @click="closeAddUserForm">
            <i class="fas fa-times"></i>
          </button>
        </div>
        <form @submit.prevent="addUser">
          <div class="form-group">
            <label class="form-label" for="new-username">{{ $t('userManagement.username') }}</label>
            <input type="text" class="form-control" id="new-username" v-model="newUser.username" @input="validateUsername" required>
            <div class="text-danger" v-if="usernameError">{{ usernameError }}</div>
          </div>
          
          <div class="form-row">
            <div class="form-group">
              <label class="form-label" for="new-first-name">{{ $t('userManagement.firstName') }}</label>
              <input type="text" class="form-control" id="new-first-name" v-model="newUser.first_name" required>
            </div>
            <div class="form-group">
              <label class="form-label" for="new-last-name">{{ $t('userManagement.lastName') }}</label>
              <input type="text" class="form-control" id="new-last-name" v-model="newUser.last_name" required>
            </div>
          </div>
          
          <div class="form-group">
            <label class="form-label" for="new-email">{{ $t('userManagement.email') }}</label>
            <input type="email" class="form-control" id="new-email" v-model="newUser.email" @input="validateEmail" required>
            <div class="text-danger" v-if="emailError">{{ emailError }}</div>
          </div>
          
          <div class="form-row">
            <div class="form-group">
              <label class="form-label" for="new-role">{{ $t('userManagement.role') }}</label>
              <select class="form-control" id="new-role" v-model="newUser.role" required>
                <option value="student">{{ $t('userManagement.student') }}</option>
                <option value="teacher">{{ $t('userManagement.teacher') }}</option>
                <option value="senior_teacher">{{ $t('userManagement.senior_teacher') }}</option>
                <option value="admin">{{ $t('userManagement.admin') }}</option>
              </select>
            </div>
            
            <div class="form-group">
              <label class="form-label" for="new-password">{{ $t('userManagement.password') }}</label>
              <input type="password" class="form-control" id="new-password" v-model="newUser.password" @input="() => validatePassword(true)">
              <div class="text-danger" v-if="passwordError">{{ passwordError }}</div>
            </div>
          </div>
          
          <div class="form-buttons">
            <button type="button" class="btn-secondary" @click="closeAddUserForm">
              {{ $t('userManagement.cancel') }}
            </button>
            <button type="submit" class="btn-primary">
              {{ $t('userManagement.addUser') }}
            </button>
          </div>
        </form>
      </div>
    </div>

    <!-- Edit User Form -->
    <div class="overlay" v-if="showEditUserForm">
      <div class="form-popup">
        <div class="form-header">
          <h2>{{ $t('userManagement.editUser') }}</h2>
          <button class="btn-close" @click="closeEditUserForm">
            <i class="fas fa-times"></i>
          </button>
        </div>
        <form @submit.prevent="updateUser">
          <div class="form-group">
            <label class="form-label" for="edit-username">{{ $t('userManagement.username') }}</label>
            <input type="text" class="form-control" id="edit-username" v-model="newUser.username" @input="validateUsername" required>
            <div class="text-danger" v-if="usernameError">{{ usernameError }}</div>
          </div>
          
          <div class="form-row">
            <div class="form-group">
              <label class="form-label" for="edit-first-name">{{ $t('userManagement.firstName') }}</label>
              <input type="text" class="form-control" id="edit-first-name" v-model="newUser.first_name" required>
            </div>
            <div class="form-group">
              <label class="form-label" for="edit-last-name">{{ $t('userManagement.lastName') }}</label>
              <input type="text" class="form-control" id="edit-last-name" v-model="newUser.last_name" required>
            </div>
          </div>
          
          <div class="form-group">
            <label class="form-label" for="edit-email">{{ $t('userManagement.email') }}</label>
            <input type="email" class="form-control" id="edit-email" v-model="newUser.email" @input="validateEmail" required>
            <div class="text-danger" v-if="emailError">{{ emailError }}</div>
          </div>
          
          <div class="form-row">
            <div class="form-group">
              <label class="form-label" for="edit-role">{{ $t('userManagement.role') }}</label>
              <select class="form-control" id="edit-role" v-model="newUser.role" required>
                <option value="student">{{ $t('userManagement.student') }}</option>
                <option value="teacher">{{ $t('userManagement.teacher') }}</option>
                <option value="senior_teacher">{{ $t('userManagement.senior_teacher') }}</option>
                <option value="admin">{{ $t('userManagement.admin') }}</option>
              </select>
            </div>
            
            <div class="form-group">
              <label class="form-label" for="discount">{{ $t('userManagement.discountLabel') }}</label>
              <input 
                type="number" 
                class="form-control" 
                id="discount" 
                v-model.number="newUser.discount" 
                step="1"
                min="0"
                max="100"
              />
              <small class="form-hint">{{ $t('userManagement.discountHelp') }}</small>
            </div>
          </div>
          
          <div class="form-group">
            <label class="form-label" for="edit-balance">{{ $t('userManagement.balance') }}</label>
            <input type="number" class="form-control" id="edit-balance" v-model="newUser.balance" required>
          </div>
          
          <div class="form-buttons">
            <button type="button" class="btn-secondary" @click="closeEditUserForm">
              {{ $t('userManagement.cancel') }}
            </button>
            <button type="submit" class="btn-primary">
              {{ $t('userManagement.updateUser') }}
            </button>
          </div>
        </form>
      </div>
    </div>

    <!-- Change Password Form -->
    <div class="overlay" v-if="showChangePasswordForm">
      <div class="form-popup">
        <div class="form-header">
          <h2>{{ $t('userManagement.changePassword') }} - {{ newUser.first_name }} {{ newUser.last_name }}</h2>
          <button class="btn-close" @click="closeChangePasswordForm">
            <i class="fas fa-times"></i>
          </button>
        </div>
        <form @submit.prevent="changePassword">
          <div class="form-group">
            <label class="form-label" for="new-password">{{ $t('userManagement.newPassword') }}</label>
            <input 
              type="password" 
              class="form-control" 
              id="new-password" 
              v-model="passwords.new" 
              @input="validatePasswordRequirements"
              required
            >
            <!-- Password requirements indicator -->
            <div class="password-requirements">
              <div class="requirement" :class="{ met: passwordValidation.length }">
                <i class="fas" :class="passwordValidation.length ? 'fa-check' : 'fa-times'"></i>
                {{ $t('userManagement.minLength') }}
              </div>
              <div class="requirement" :class="{ met: passwordValidation.uppercase }">
                <i class="fas" :class="passwordValidation.uppercase ? 'fa-check' : 'fa-times'"></i>
                {{ $t('userManagement.requireUppercase') }}
              </div>
              <div class="requirement" :class="{ met: passwordValidation.lowercase }">
                <i class="fas" :class="passwordValidation.lowercase ? 'fa-check' : 'fa-times'"></i>
                {{ $t('userManagement.requireLowercase') }}
              </div>
              <div class="requirement" :class="{ met: passwordValidation.number }">
                <i class="fas" :class="passwordValidation.number ? 'fa-check' : 'fa-times'"></i>
                {{ $t('userManagement.requireNumber') }}
              </div>
            </div>
          </div>
          <div class="form-group">
            <label class="form-label" for="confirm-password">{{ $t('userManagement.confirmNewPassword') }}</label>
            <input 
              type="password" 
              class="form-control" 
              id="confirm-password" 
              v-model="passwords.confirm" 
              @input="validatePasswordMatch"
              required
            >
            <div class="text-danger" v-if="passwordMatchError">{{ passwordMatchError }}</div>
          </div>
          <div class="form-buttons">
            <button type="button" class="btn-secondary" @click="closeChangePasswordForm">
              {{ $t('userManagement.cancel') }}
            </button>
            <button 
              type="submit" 
              class="btn-primary" 
              :disabled="!isPasswordValid || !passwordsMatch"
            >
              {{ $t('userManagement.updatePassword') }}
            </button>
          </div>
        </form>
      </div>
    </div>

    <!-- Toast Notification -->
    <div class="toast-notification" v-if="notificationVisible">
      <i class="fas fa-check-circle"></i>
      <span class="toast-message">{{ notificationMessage }}</span>
    </div>
  </div>
</template>

<script>
import axiosInstance from '../router/axiosSetup';
export default {
  data() {
    return {
      activeTab: 'users',

      // Archive specific
      archivedUsers: [],
      archiveLoading: false,
      archiveSearchQuery: '',
      archiveCurrentPage: 1,
      archivePageSize: 10,
      archiveTotalUsers: 0,
      archiveSortKey: 'archived_at',
      archiveSortOrder: 'DESC',

      searchQuery: '',
      users: [],
      showAddUserForm: false,
      showEditUserForm: false,
      showChangePasswordForm: false,
      newUser: {
        id: null,
        username: '',
        first_name: '',
        last_name: '',
        email: '',
        role: 'student',
        password: '',
        balance: 0,
        discount: 0,
      },
      passwords: {
        new: '',
        confirm: ''
      },
      initialBalance: 0,
      notificationVisible: false,
      notificationMessage: '',
      sortKey: 'username',
      sortOrder: 1,
      currentPage: 1,
      pageSize: 10,
      usernameError: '',
      emailError: '',
      passwordError: '',
      originalUsername: '',
      isLoading: false,  // Track loading state
      searchUserId: null,
      searchSuggestions: [],
      showSuggestions: false,
      totalUsers: 0,
      passwordValidation: {
        length: false,
        uppercase: false,
        lowercase: false,
        number: false
      },
      passwordMatchError: '',
    };
  },
  watch:{
    '$route.query': {
      handler(newQuery) {
        const userId = newQuery.userId;
        if (userId) {
          this.searchUserId = userId;
          this.searchUserById(userId);
        } else {
          // Reset to normal view if userId is removed from query
          this.searchUserId = null;
          this.searchUsers(); // Your existing method to fetch all users
        }
      },
      immediate: true
    }
  },
  computed: {
    sortedUsers() {
      return this.users.slice().sort((a, b) => {
        let result = 0;
        if (this.sortKey === 'balance') {
          result = a[this.sortKey] - b[this.sortKey];
        } else {
          if (a[this.sortKey] < b[this.sortKey]) {
            result = -1;
          } else if (a[this.sortKey] > b[this.sortKey]) {
            result = 1;
          }
        }
        return result * this.sortOrder;
      });
    },
    totalPages() {
      return Math.max(1, Math.ceil(this.totalUsers / this.pageSize));
    },
    archiveTotalPages() {
      return Math.max(1, Math.ceil(this.archiveTotalUsers / this.archivePageSize));
    },
    isPasswordValid() {
      return this.passwordValidation.length && 
             this.passwordValidation.uppercase && 
             this.passwordValidation.lowercase && 
             this.passwordValidation.number;
    },
    
    passwordsMatch() {
      return this.passwords.new === this.passwords.confirm && this.passwords.confirm !== '';
    }
  },
  methods: {
    handlePageSizeChange() {
      // Reset to first page when changing page size
      this.currentPage = 1;
      this.searchUsers();
      
      // Store preference in localStorage
      localStorage.setItem('userManagementPageSize', this.pageSize);
    },
    async fetchUserSuggestions() {
      // Don't fetch suggestions if query is too short
      if (this.searchQuery.length < 2) {
        this.searchSuggestions = [];
        return;
      }
      
      try {
        const response = await axiosInstance.get(`/admin/user-suggestions?query=${this.searchQuery}`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        });
        
        this.searchSuggestions = response.data.map(user => this.normalizeUserData(user));
        // Show suggestions if there are any
        this.showSuggestions = this.searchSuggestions.length > 0;
      } catch (error) {
        console.error('Error fetching user suggestions:', error);
        this.searchSuggestions = [];
      }
    },
    selectUser(user) {
      // Set search query to the selected user
      this.searchQuery = `${user.first_name} ${user.last_name}`;
      // Hide suggestions
      this.showSuggestions = false;
      // Redirect to user-specific view
      this.searchUserById(user.id);
    },
    showNotification(message) {
      this.notificationMessage = message || this.$t('userManagement.userUpdated');
      this.notificationVisible = true;
      // Auto-hide after 3 seconds
      setTimeout(() => {
        this.notificationVisible = false;
      }, 3000);
    },
    normalizeUserData(user) {
      // Ensure discount exists and is a number
      if (user.discount === undefined || user.discount === null) {
        user.discount = 0;
      } else {
        user.discount = Number(user.discount);
      }
      return user;
    },
    sortUsers(key) {
      if (this.sortKey === key) {
        this.sortOrder *= -1;
      } else {
        this.sortKey = key;
        this.sortOrder = 1;
      }
      this.searchUsers();
    },
    setupClickOutside() {
      document.addEventListener('click', this.handleClickOutside);
    },
    
    handleClickOutside(event) {
      const searchContainer = this.$el.querySelector('.search-container');
      if (searchContainer && !searchContainer.contains(event.target)) {
        this.showSuggestions = false;
      }
    },
    async searchUsers() {
      try {
        this.isLoading = true;
        let queryParams = `query=${this.searchQuery}&limit=${this.pageSize}&offset=${(this.currentPage - 1) * this.pageSize}&sortKey=${this.sortKey}&sortOrder=${this.sortOrder === 1 ? 'ASC' : 'DESC'}`;
        
        // If userId is provided, add it to query
        if (this.searchUserId) {
          queryParams += `&userId=${this.searchUserId}`;
        }
        
        const response = await axiosInstance.get(`/admin/search-users?${queryParams}`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        });
        
        // Process users data
        this.users = response.data.users.map(user => this.normalizeUserData(user));
        this.totalUsers = response.data.total || this.users.length;
        this.isLoading = false;
        
        // Hide suggestions after search
        this.showSuggestions = false;
      } catch (error) {
        console.error('Error searching users:', error);
        this.isLoading = false;
        this.users = []; // Clear users on error
        this.totalUsers = 0;
      }
    },
    validateUsername() {
      if (this.newUser.username.length < 5) {
        this.usernameError = 'Username must be at least 5 characters long.';
      } else {
        this.usernameError = '';
        if (this.newUser.username !== this.originalUsername) {
          this.checkUsernameExists();
        }
      }
    },
    async checkUsernameExists() {
      try {
        const response = await axiosInstance.get(`/check-username?username=${this.newUser.username}`);
        if (response && response.data) {
          if (response.data.exists) {
            this.usernameError = 'Username is already taken.';
          } else {
            this.usernameError = '';
          }
        }
      } catch (error) {
        console.error('Error checking username:', error);
        // Handle error gracefully, maybe set a generic error message
        this.usernameError = 'Unable to verify username availability.';
      }
    },
    validateEmail() {
      const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailPattern.test(this.newUser.email)) {
        this.emailError = 'Please enter a valid email address.';
      } else {
        this.emailError = '';
      }
    },
    validatePassword(isNewUser = true) {
      const passwordPattern = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)[A-Za-z\d]{8,}$/;
      
      // Check which password field to validate based on context
      const passwordToValidate = isNewUser ? this.newUser.password : this.passwords.new;
      
      if (!passwordPattern.test(passwordToValidate)) {
        this.passwordError = 'Password must contain at least 1 lowercase letter, 1 uppercase letter, and 1 number.';
      } else {
        this.passwordError = '';
      }
    },
    async addUser() {
      this.validateUsername();
      this.validateEmail();
      this.validatePassword(true);
      if (this.usernameError || this.emailError || this.passwordError) {
        return; // Prevent form submission if there are validation errors
      }
      try {
        await axiosInstance.post('/admin/add-user', this.newUser, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        });
        this.showNotification(this.$t('userManagement.userAdded')); // Use custom message
        this.notificationVisible = true; // Show success notification
        this.searchUsers(); // Refresh the user list after adding
        this.resetNewUser(); // Reset add user form
        this.showAddUserForm = false; // Hide the form
      } catch (error) {
        console.error('Error adding user:', error);
      }
    },
    async updateUser() {
      this.validateUsername();
      this.validateEmail();
      if (this.newUser.password) {
        this.validatePassword();
      }
      if (this.usernameError || this.emailError || this.passwordError) {
        return; // Prevent form submission if there are validation errors
      }
      try {
        const updatedUser = { ...this.newUser };
        if (!this.newUser.password) {
          delete updatedUser.password; // Remove password field if it's empty
        }
        await axiosInstance.put('/admin/update-user', updatedUser, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        });

        if (this.newUser.balance !== this.initialBalance) {
          const amount = this.newUser.balance - this.initialBalance;
          await axiosInstance.post('/admin/create-transaction', {
            userId: this.newUser.id,
            amount: amount,
            balanceAfter: this.newUser.balance
          }, {
            headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
          });
        }
        this.showNotification(this.$t('userManagement.userUpdated'));
        this.notificationVisible = true; // Show success notification
        this.searchUsers(); // Refresh the user list after updating
        this.resetNewUser(); // Reset add user form
        this.showEditUserForm = false; // Hide the form
      } catch (error) {
        console.error('Error updating user:', error);
      }
    },
    validatePasswordRequirements() {
      const password = this.passwords.new;
      
      // Check minimum length (8 characters)
      this.passwordValidation.length = password.length >= 8;
      
      // Check for at least one uppercase letter
      this.passwordValidation.uppercase = /[A-Z]/.test(password);
      
      // Check for at least one lowercase letter
      this.passwordValidation.lowercase = /[a-z]/.test(password);
      
      // Check for at least one number
      this.passwordValidation.number = /\d/.test(password);
      
      // Also validate password match if confirm is not empty
      if (this.passwords.confirm) {
        this.validatePasswordMatch();
      }
    },
    validatePasswordMatch() {
      if (this.passwords.new !== this.passwords.confirm) {
        this.passwordMatchError = this.$t('userManagement.passwordsDoNotMatch');
      } else {
        this.passwordMatchError = '';
      }
    },
    
    async changePassword() {
      if (!this.isPasswordValid || !this.passwordsMatch) {
        return; // Prevent form submission if validation fails
      }
      
      try {
        await axiosInstance.put('/admin/update-user-password', {
          userId: this.newUser.id,
          password: this.passwords.new
        }, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        });
        
        this.showNotification(this.$t('userManagement.passwordChanged'));
        this.closeChangePasswordForm(); // Hide the form on success
      } catch (error) {
        console.error('Error changing password:', error);
        this.showNotification(this.$t('userManagement.errorChangingPassword'), 'error');
      }
    },
    startChangePassword(user) {
      this.newUser = { ...user, password: '' }; // Populate the form with user data, clear password
      this.passwords.new = '';
      this.passwords.confirm = '';
      this.showChangePasswordForm = true; // Display the change password form
    },

    closeChangePasswordForm() {
      this.showChangePasswordForm = false;
      this.passwords.new = '';
      this.passwords.confirm = '';
      this.passwordValidation = {
        length: false,
        uppercase: false,
        lowercase: false,
        number: false
      };
      this.passwordMatchError = '';
    },
    async deleteUser(userId) {
        try {
          await axiosInstance.delete('/admin/delete-user', {
            data: { id: userId },
            headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
          });
          this.showNotification(this.$t('userManagement.userArchived'));
          await this.searchUsers(); // Refresh the user list after deletion
        } catch (error) {
          console.error('Error archiving user:', error);
        }
    },
    resetNewUser() {
      this.newUser = {
        id: null,
        username: '',
        first_name: '',
        last_name: '',
        email: '',
        role: 'student',
        password: '',
        balance: 0,
      };
      this.initialBalance = 0;
    },
    startEditUser(user) {
      console.log('Editing user:', user);
      this.newUser = { ...user, password: '' }; // Populate the form with user data, clear password
      this.initialBalance = user.balance;
      this.originalUsername = user.username;
      this.showEditUserForm = true; // Display the edit user form
    },
    closeEditUserForm() {
      this.showEditUserForm = false; // Hide the edit user form
    },
    closeAddUserForm() {
      this.showAddUserForm = false; // Hide the add user form
    },
    toggleAddUserForm() {
      this.showAddUserForm = !this.showAddUserForm; // Toggle the add user form visibility
    },
    nextPage() {
      this.currentPage++;
      this.searchUsers();
    },
    prevPage() {
      if (this.currentPage > 1) {
        this.currentPage--;
        this.searchUsers();
      }
    },
    searchUserById(userId) {
      this.isLoading = true;
      
      axiosInstance.get('/admin/search-users', {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
        params: {
          userId: userId,
          limit: this.pageSize,
          offset: 0
        }
      })
      .then(response => {
        // The search-users endpoint returns { users: [...], total: n }
        this.users = response.data.users || [];
        this.totalUsers = response.data.total || this.users.length;
        this.isLoading = false;
      })
      .catch(error => {
        console.error('Error fetching user:', error);
        this.isLoading = false;
      });
    },
    switchTab(tab) {
      this.activeTab = tab;
      if (tab === 'archive' && this.archivedUsers.length === 0) {
        this.fetchArchivedUsers();
      }
    },
    async fetchArchivedUsers() {
      this.archiveLoading = true;
      try {
        const response = await axiosInstance.get('/admin/archived-users', {
          params: {
            query: this.archiveSearchQuery,
            limit: this.archivePageSize,
            offset: (this.archiveCurrentPage - 1) * this.archivePageSize,
            sortKey: this.archiveSortKey,
            sortOrder: this.archiveSortOrder,
          },
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        });
        this.archivedUsers = response.data.users;
        this.archiveTotalUsers = response.data.total;
      } catch (error) {
        console.error('Error fetching archived users:', error);
      } finally {
        this.archiveLoading = false;
      }
    },
    async restoreUser(userId) {
      console.log('restoreUser called with userId:', userId); // 👈 ADD THIS
      if (!confirm(this.$t('userManagement.restoreUserConfirm'))) return;
      try {
        console.log('Sending patch request...'); // 👈 ADD THIS
        const response = await axiosInstance.patch(`/admin/restore-user/${userId}`, {}, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        });
        console.log('Response:', response); // 👈 ADD THIS
        this.archivedUsers = this.archivedUsers.filter(u => u.id !== userId);
        this.archiveTotalUsers--;
        this.showNotification(this.$t('userManagement.userRestored'));
      } catch (error) {
        console.error('Error restoring user:', error); // already there
      }
    },
    sortArchive(key) {
      if (this.archiveSortKey === key) {
        this.archiveSortOrder = this.archiveSortOrder === 'ASC' ? 'DESC' : 'ASC';
      } else {
        this.archiveSortKey = key;
        this.archiveSortOrder = 'ASC';
      }
      this.archiveCurrentPage = 1;
      this.fetchArchivedUsers();
    },
    handleArchivePageSizeChange() {
      this.archiveCurrentPage = 1;
      this.fetchArchivedUsers();
      localStorage.setItem('archivePageSize', this.archivePageSize);
    },
    archiveNextPage() {
      this.archiveCurrentPage++;
      this.fetchArchivedUsers();
    },
    archivePrevPage() {
      if (this.archiveCurrentPage > 1) {
        this.archiveCurrentPage--;
        this.fetchArchivedUsers();
      }
    },
  },
  beforeUnmount() {
    document.removeEventListener('click', this.handleClickOutside);
  },
  created() {
    const savedPageSize = localStorage.getItem('userManagementPageSize');
    if (savedPageSize) {
      this.pageSize = parseInt(savedPageSize);
    }
    const userId = this.$route.query.userId;
    if (userId) {
      // Set the search term to filter for just this user
      this.searchUserId = userId;
      // Trigger the search immediately
      this.searchUserById(userId);
    } else {
      this.searchUsers(); 
    }// Populate users list on component creation
    this.setupClickOutside();
  },
};
</script>

<style scoped>
/* Base layout */
.user-management-container {
  padding: 24px;
  max-width: 1400px;
  margin: 0 auto;
  color: #333;
}

/* Page header */
.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
}

.page-title {
  font-size: 1.75rem;
  font-weight: 600;
  color: #212529;
  margin: 0;
}

/* Search container */
.search-container {
  position: relative;
  margin-bottom: 24px;
  display: flex;
  align-items: center;
  max-width: 800px;
}

.search-icon {
  position: absolute;
  left: 14px;
  top: 50%;
  transform: translateY(-50%);
  color: #64748b;
  font-size: 16px;
  pointer-events: none;
}

.search-input {
  flex: 1;
  padding: 10px 12px 10px 40px;
  height: 42px;
  border-radius: 21px;
  border: 1px solid #e2e8f0;
  font-size: 0.95rem;
  transition: all 0.2s;
}

.search-input:focus {
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.25);
  outline: none;
}

.btn-search {
  margin-left: 10px;
  padding: 8px 16px;
  background-color: #3b82f6;
  color: white;
  border: none;
  border-radius: 21px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-search:hover {
  background-color: #2563eb;
}

/* Table styling */
.table-responsive {
  overflow-x: auto;
  margin-bottom: 24px;
  border-radius: 8px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
}

.user-table {
  width: 100%;
  border-collapse: collapse;
  overflow: hidden;
}

.user-table th {
  padding: 16px 12px;
  font-weight: 600;
  color: #495057;
  border-bottom: 2px solid #dee2e6;
  background-color: #f8f9fa;
  text-align: left;
  cursor: pointer;
  user-select: none;
  transition: background-color 0.2s;
}

.user-table th:hover {
  background-color: #e9ecef;
}

.sort-indicator {
  margin-left: 8px;
  opacity: 0.5;
}

.sort-indicator.active {
  opacity: 1;
}

.user-table tbody tr {
  transition: background-color 0.2s;
  border-bottom: 1px solid #edf2f7;
}

.user-table tbody tr:hover {
  background-color: #f1f5f9;
}

.user-table td {
  padding: 14px 12px;
  vertical-align: middle;
}

/* Role badges */
.role-badge {
  padding: 4px 10px;
  border-radius: 20px;
  font-size: 0.85rem;
  font-weight: 500;
  display: inline-block;
  text-transform: capitalize;
}

.role-student {
  background-color: #e3f2fd;
  color: #0d47a1;
}

.role-teacher {
  background-color: #e8f5e9;
  color: #1b5e20;
}

.role-senior_teacher {
  background-color: #fff8e1;
  color: #f57f17;
}

.role-admin {
  background-color: #fce4ec;
  color: #880e4f;
}

/* Status badges */
.status-badge {
  padding: 4px 10px;
  border-radius: 20px;
  font-size: 0.85rem;
  font-weight: 500;
  display: inline-block;
}

.status-active {
  background-color: #e8f5e9;
  color: #1b5e20;
}

.status-inactive {
  background-color: #f5f5f5;
  color: #616161;
}

.status-unknown {
  background-color: #ffecb3;
  color: #ff6f00;
}

/* Balance styling */
.balance-cell {
  font-family: 'Roboto Mono', monospace;
  font-weight: 500;
}

.balance-positive {
  color: #2e7d32;
}

.balance-negative {
  color: #c62828;
}

.balance-zero {
  color: #757575;
}

/* Action buttons */
.action-buttons {
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
}

.btn-action {
  padding: 6px 10px;
  border-radius: 6px;
  font-size: 0.85rem;
  transition: all 0.2s;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 1px 3px rgba(0,0,0,0.05);
  cursor: pointer;
  white-space: nowrap;
}

.btn-action i {
  margin-right: 4px;
  font-size: 0.9rem;
}

.btn-edit {
  background-color: #fff;
  color: #3b82f6;
  border: 1px solid #3b82f6;
}

.btn-edit:hover {
  background-color: #3b82f6;
  color: #fff;
}

.btn-password {
  background-color: #fff;
  color: #6b7280;
  border: 1px solid #6b7280;
}

.btn-password:hover {
  background-color: #6b7280;
  color: #fff;
}

.btn-delete {
  background-color: #fff;
  color: #dc2626;
  border: 1px solid #dc2626;
}

.btn-delete:hover {
  background-color: #dc2626;
  color: #fff;
}

/* Pagination styles */
.pagination-container {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin: 24px 0;
  flex-wrap: wrap;
  gap: 16px;
}
.pagination-controls {
  display: flex;
  align-items: center;
  gap: 20px;
}
.per-page-selector {
  display: flex;
  align-items: center;
  gap: 8px;
}
.per-page-selector label {
  color: #4b5563;
  font-weight: 500;
  white-space: nowrap;
}
.page-size-select {
  padding: 6px 10px;
  border-radius: 6px;
  border: 1px solid #d1d5db;
  background-color: #fff;
  color: #4b5563;
  cursor: pointer;
  min-width: 70px;
}

.page-size-select:focus {
  border-color: #3b82f6;
  outline: none;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.25);
}.page-info {
  color: #4b5563;
  font-size: 0.9rem;
}

/* Update the existing pagination-nav class */
.pagination-nav {
  display: flex;
  align-items: center;
  gap: 12px;
}
@media (max-width: 768px) {
  .pagination-container {
    flex-direction: column;
    align-items: center;
  }
  
  .pagination-controls {
    flex-direction: column;
    gap: 12px;
    width: 100%;
  }
}
.page-status {
  padding: 6px 12px;
  background-color: #f8f9fa;
  border-radius: 6px;
  color: #495057;
  font-weight: 500;
}

.page-btn {
  padding: 6px 12px;
  border: 1px solid #dee2e6;
  background-color: #fff;
  color: #495057;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
}

.page-btn:hover:not(:disabled) {
  background-color: #e9ecef;
  border-color: #ced4da;
}

.page-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.page-btn i {
  font-size: 0.9rem;
}

/* Empty state */
.empty-state {
  padding: 48px 0;
  text-align: center;
  color: #6b7280;
}

.empty-state i {
  font-size: 3.5rem;
  margin-bottom: 16px;
  color: #d1d5db;
}

.empty-state p {
  font-size: 1.1rem;
}

/* Form styles */
.overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  animation: fadeIn 0.2s ease;
}

@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

.form-popup {
  background: white;
  padding: 24px;
  border-radius: 10px;
  width: 500px;
  max-width: 90%;
  max-height: 90vh;
  overflow-y: auto;
  z-index: 1001;
  box-shadow: 0 10px 25px rgba(0,0,0,0.15);
  animation: slideUp 0.3s ease;
}

@keyframes slideUp {
  from { transform: translateY(30px); opacity: 0; }
  to { transform: translateY(0); opacity: 1; }
}

.form-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
}

.form-header h2 {
  margin: 0;
  color: #212529;
  font-weight: 600;
  font-size: 1.5rem;
}

.btn-close {
  background: none;
  border: none;
  cursor: pointer;
  font-size: 1.25rem;
  color: #6b7280;
  padding: 4px;
}

.btn-close:hover {
  color: #1f2937;
}

.form-group {
  margin-bottom: 16px;
}

.form-row {
  display: flex;
  gap: 16px;
  margin-bottom: 16px;
}

.form-row .form-group {
  flex: 1;
  margin-bottom: 0;
}

.form-label {
  display: block;
  margin-bottom: 6px;
  font-weight: 500;
  color: #4b5563;
}

.form-control {
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 0.95rem;
  transition: border-color 0.15s, box-shadow 0.15s;
}

.form-control:focus {
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.25);
  outline: none;
}

.form-hint {
  display: block;
  margin-top: 4px;
  color: #6b7280;
  font-size: 0.875rem;
}

.form-buttons {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 24px;
}

.btn-primary {
  background-color: #3b82f6;
  color: white;
  border: none;
  border-radius: 6px;
  padding: 9px 16px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-primary:hover {
  background-color: #2563eb;
  box-shadow: 0 2px 5px rgba(37, 99, 235, 0.3);
}

.btn-secondary {
  background-color: white;
  color: #4b5563;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  padding: 8px 16px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-secondary:hover {
  background-color: #f9fafb;
  border-color: #9ca3af;
}

.text-danger {
  color: #dc2626;
  font-size: 0.875rem;
  margin-top: 4px;
}

/* Toast notification */
.toast-notification {
  position: fixed;
  bottom: 24px;
  right: 24px;
  padding: 12px 16px;
  background-color: white;
  border-left: 4px solid #10b981;
  border-radius: 6px;
  box-shadow: 0 4px 12px rgba(0,0,0,0.1);
  display: flex;
  align-items: center;
  min-width: 300px;
  z-index: 1050;
  animation: slideInRight 0.3s, fadeOut 0.3s 2.7s forwards;
}

.toast-notification i {
  font-size: 1.25rem;
  margin-right: 12px;
  color: #10b981;
}

.toast-message {
  font-size: 0.95rem;
  color: #1f2937;
}

@keyframes slideInRight {
  from { transform: translateX(100%); opacity: 0; }
  to { transform: translateX(0); opacity: 1; }
}

@keyframes fadeOut {
  from { opacity: 1; }
  to { opacity: 0; }
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .form-row {
    flex-direction: column;
    gap: 0;
  }
  
  .action-buttons {
    flex-direction: column;
    gap: 8px;
  }
  
  .btn-action {
    width: 100%;
  }
  
  .user-table th,
  .user-table td {
    padding: 10px 8px;
    font-size: 0.9rem;
  }
}
/* Add to your <style> section */
.search-container {
  position: relative;
  margin-bottom: 24px;
  display: flex;
  align-items: center;
  max-width: 800px;
}

/* Existing search styles... */

.search-suggestions {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  max-height: 300px;
  overflow-y: auto;
  background: white;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0,0,0,0.1);
  z-index: 100;
  margin-top: 4px;
}

.suggestion-item {
  padding: 10px 12px;
  line-height: 1.5;
  white-space: normal;
  min-height: 40px;
  display: flex;
  align-items: center;
}

.suggestion-item:last-child {
  border-bottom: none;
}

.suggestion-item:hover {
  background-color: #f8fafc;
}

.suggestion-name {
  font-weight: 500;
  margin-bottom: 2px;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.role-tag {
  font-size: 0.7rem;
  padding: 2px 6px;
  border-radius: 12px;
  font-weight: 500;
}

.role-student {
  background-color: #e3f2fd;
  color: #0d47a1;
}

.role-teacher {
  background-color: #e8f5e9;
  color: #1b5e20;
}

.role-senior_teacher {
  background-color: #fff8e1;
  color: #f57f17;
}

.role-admin {
  background-color: #fce4ec;
  color: #880e4f;
}

.suggestion-details {
  display: flex;
  font-size: 0.85rem;
  color: #64748b;
  gap: 8px;
}

.suggestion-username {
  color: #3b82f6;
}
select.form-control {
  height: auto !important;
  padding: 8px 12px;
  min-height: 42px;
}

/* Fix for dropdown options being cut off */
option {
  padding: 10px;
  font-size: 14px;
  line-height: 1.5;
}

/* These styles target Webkit browsers like Chrome and Safari */
select {
  text-indent: 3px;
  text-overflow: '';
}

/* For Firefox */
@-moz-document url-prefix() {
  select.form-control option {
    padding-top: 5px;
    padding-bottom: 5px;
  }
}

/* For IE/Edge */
select::-ms-expand {
  display: none;
}
.password-requirements {
  margin-top: 8px;
  padding: 8px 0;
}

.requirement {
  display: flex;
  align-items: center;
  margin: 4px 0;
  color: #6b7280;
  font-size: 0.875rem;
}

.requirement i {
  margin-right: 6px;
  font-size: 0.75rem;
}

.requirement i.fa-times {
  color: #ef4444;
}

.requirement.met {
  color: #10b981;
}

.requirement.met i {
  color: #10b981;
}

/* Tab styles */
.tab-container {
  display: flex;
  gap: 8px;
  margin-bottom: 24px;
  border-bottom: 2px solid #e2e8f0;
  padding-bottom: 0;
}

.tab-btn {
  padding: 10px 20px;
  border: none;
  background: none;
  cursor: pointer;
  font-size: 0.95rem;
  font-weight: 500;
  color: #6b7280;
  border-bottom: 2px solid transparent;
  margin-bottom: -2px;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  gap: 8px;
}

.tab-btn:hover {
  color: #3b82f6;
}

.tab-btn-active {
  color: #3b82f6;
  border-bottom-color: #3b82f6;
}

/* Restore button */
.btn-restore {
  background-color: #fff;
  color: #10b981;
  border: 1px solid #10b981;
}

.btn-restore:hover {
  background-color: #10b981;
  color: #fff;
}
</style>